<script setup lang="ts">
import { resetPassword } from '@/api/auth'
import { ApiError } from '@/api/client'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { themeConfig } from '@themeConfig'

definePage({
  meta: { layout: 'blank', public: true, publicOnly: true },
})

const router = useRouter()

const email = ref('')
const code = ref('')
const newPassword = ref('')
const confirmPassword = ref('')
const isPasswordVisible = ref(false)
const loading = ref(false)
const error = ref('')

// Mismas reglas que valida el backend: 6+ caracteres y al menos una mayúscula.
const passwordRulesOk = computed(() => newPassword.value.length >= 6 && /[A-Z]/.test(newPassword.value))
const passwordsMatch = computed(() => !!newPassword.value && newPassword.value === confirmPassword.value)

onMounted(() => {
  email.value = sessionStorage.getItem('bt_reset_email') || ''
})

const submit = async () => {
  error.value = ''
  if (!email.value.trim() || !code.value.trim()) {
    error.value = 'Email and code are required.'

    return
  }
  if (!passwordRulesOk.value) {
    error.value = 'Password must be at least 6 characters and include one uppercase letter.'

    return
  }
  if (!passwordsMatch.value) {
    error.value = 'Passwords do not match.'

    return
  }

  loading.value = true
  try {
    await resetPassword(email.value.trim(), code.value.trim(), newPassword.value)
    sessionStorage.removeItem('bt_reset_email')
    router.push({ name: 'login' })
  }
  catch (e) {
    error.value = e instanceof ApiError ? e.message : 'Reset failed.'
  }
  finally {
    loading.value = false
  }
}
</script>

<template>
  <RouterLink to="/">
    <div class="auth-logo d-md-none d-flex align-center gap-x-3">
      <VNodeRenderer :nodes="themeConfig.app.logo" />
      <h1 class="auth-title">
        {{ themeConfig.app.title }}
      </h1>
    </div>
  </RouterLink>

  <VRow
    class="auth-wrapper"
    no-gutters
  >
    <AuthIllustration />

    <VCol
      cols="12"
      md="4"
      class="auth-card-v2 d-flex align-center justify-center"
      style="background-color: rgb(var(--v-theme-surface));"
    >
      <VCard
        flat
        :max-width="500"
        class="mt-12 mt-sm-0 pa-4"
      >
        <VCardText>
          <h4 class="text-h4 mb-1">
            Reset Password 🔑
          </h4>
          <p class="mb-0">
            Enter the code we sent you and choose a new password.
          </p>
        </VCardText>

        <VCardText>
          <VForm @submit.prevent="submit">
            <VRow>
              <VCol
                v-if="error"
                cols="12"
              >
                <VAlert
                  type="error"
                  variant="tonal"
                  density="compact"
                >
                  {{ error }}
                </VAlert>
              </VCol>

              <VCol cols="12">
                <VTextField
                  v-model="email"
                  label="Email"
                  type="email"
                  autocomplete="email"
                  :disabled="loading"
                />
              </VCol>

              <VCol cols="12">
                <VTextField
                  v-model="code"
                  label="4-digit code"
                  type="tel"
                  inputmode="numeric"
                  maxlength="4"
                  :disabled="loading"
                />
              </VCol>

              <VCol cols="12">
                <VTextField
                  v-model="newPassword"
                  label="New password"
                  placeholder="············"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  autocomplete="new-password"
                  hint="6+ characters, with at least one uppercase letter."
                  persistent-hint
                  :disabled="loading"
                  :append-inner-icon="isPasswordVisible ? 'ri-eye-off-line' : 'ri-eye-line'"
                  @click:append-inner="isPasswordVisible = !isPasswordVisible"
                />
              </VCol>

              <VCol cols="12">
                <VTextField
                  v-model="confirmPassword"
                  label="Confirm new password"
                  placeholder="············"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  autocomplete="new-password"
                  :disabled="loading"
                />
              </VCol>

              <VCol cols="12">
                <VBtn
                  block
                  type="submit"
                  :loading="loading"
                >
                  Update password
                </VBtn>
              </VCol>

              <VCol cols="12">
                <RouterLink
                  class="d-flex align-center justify-center"
                  :to="{ name: 'login' }"
                >
                  <VIcon
                    icon="ri-arrow-left-s-line"
                    class="flip-in-rtl"
                  />
                  <span>Back to login</span>
                </RouterLink>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
</template>

<style lang="scss">
@use "@core/scss/template/pages/page-auth";
</style>
